import { Collapse, Fab, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { Avatar, OpenTasks } from "src/common/components";
import { IconArrowDropDown } from "src/common/icons";
import {
  ITalk,
  OpenTasksSubmissionStatus,
  QuestionnaireType,
  TalkStatus,
  ISubmitSessionDatePayload
} from "src/common/types";
import { dateWithoutTime, displayDate, getFullName } from "src/helpers";
import { IMyEmployee } from "src/modules/Dashboard/MyEmployees/types";
import DateStatus from "../DateStatus";
import EmployeeCardTalk from "./EmployeeCardTalk";
import Status from "./status";

const useStyles = makeStyles((theme: any) => ({
  arrow: {
    display: "inline-block" as "inline-block",
    fontSize: "1.35rem",
    lineHeight: "1",
    transform: "rotate(360deg) scale(0.9,1.35)",
    transition: "transform 0.2s linear",
    verticalAlign: "middle" as "middle"
  },
  arrowContainer: {
    [theme.breakpoints.up("md")]: {
      padding: "0 !important",
      width: 54
    },
    padding: "0 12px"
  },
  avatarContainer: {
    padding: "0px 16px !important",
    width: 74
  },
  container: {
    [theme.breakpoints.up("md")]: {
      "& td": {
        lineHeight: "59px"
      },
      marginBottom: 18
    },
    marginBottom: 8
  },
  dateWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 110,
    padding: "0 8px",
    width: 110,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  employeeContainer: {
    background: "#ffffff",
    border: "1px solid #e6e6e6",
    borderRadius: 4,
    boxShadow: "0 2px 4px 0 rgba(219, 219, 219, 0.5)",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    margin: "0 0 8px"
  },
  employeeCycles: {
    display: "block" as "block",
    position: "relative" as "relative",
    transition: ".3s max-height ease-out",
    width: "100%"
  },
  employeeDetails: {
    alignItem: "center" as "center",
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    justifyContent: "space-between" as "space-between",
    lineHeight: "42px",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      lineHeight: "60px"
    }
  },
  employeeDetailsAvatar: {
    padding: "0 8px 0 16px",
    width: "66px"
  },
  employeeDetailsName: {
    flexGrow: 1,
    padding: "0 8px"
  },
  employeeDetailsNameLabel: {
    cursor: "pointer" as "pointer"
  },
  expandButtonLabel: {
    height: "100%"
  },
  expandLabel: {
    alignItems: "center" as "center",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    height: "40px",
    justifyContent: "center" as "center",
    width: "40px"
  },
  expandWrapper: {
    minWidth: 54,
    padding: "0 16px 0 8px",
    width: 54
  },
  fab: {
    background: "none" as "none",
    boxShadow: "none" as "none"
  },
  mobileDates: {
    display: "block" as "block",
    fontSize: "1.2rem",
    padding: "0 8px 0 16px",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  open: {
    "& $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    }
  },
  optionsWrapper: {
    minWidth: 40,
    position: "relative" as "relative",
    width: 40
  },
  scheduleDatePlainTextWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 154,
    width: 154,
    [theme.breakpoints.up("md")]: {
      display: "flex"
    }
  },
  scheduleDateWrapper: {
    [theme.breakpoints.up("md")]: {
      alignItems: "center",
      display: "flex",
      minWidth: 154
    },
    display: "none"
  },
  statusWrapper: {
    alignItems: "center",
    display: "flex",
    padding: "0px 8px",
    [theme.breakpoints.up("md")]: {
      minWidth: 184,
      width: 184
    }
  },
  title: {
    display: "inline-block" as "inline-block",
    lineHeight: 1,
    overflowWrap: "break-word" as "break-word",
    verticalAlign: "middle" as "middle",
    wordBreak: "break-word" as "break-word"
  }
}));

interface IOwnProps {
  onScheduleTalkDateSubmit: (talkId: string, data: ISubmitSessionDatePayload, icName: string, icEmail: string) => void;
  onCancellation: (talkId: string) => void;
  onReassign: (talkId: string, cycleId: string) => void;
  onReopen: (talkId: string) => void;
  dsvName: string;
  defaultCollapsed: boolean;
  email?: string | null;
}

type Props = IOwnProps & IMyEmployee;

const SupervisorQuestionnaires = [QuestionnaireType.PeerSummary, QuestionnaireType.DirectSupervisor];

const Employee = ({
  email,
  photoBase64,
  firstName,
  lastName,
  middleName,
  talks,
  id,
  onScheduleTalkDateSubmit,
  onCancellation,
  onReassign,
  onReopen,
  dsvName,
  defaultCollapsed
}: Props) => {
  const currentDate = React.useMemo(() => dateWithoutTime(new Date()), []);
  const classes = useStyles();

  const icFullName = React.useMemo(() => getFullName([firstName, middleName, lastName]), []);
  const {
    status: firstTalkStatus,
    participantSignedDate: firstTalkParticipantSignedDate,
    supervisorSignedDate: firstTalkSupervisorSignedDate,
    talkCompletionDueDate: firstTalkCompletionDueDate
  } = talks[0] as ITalk;

  const firstTalkCompletionDatePassed = currentDate > dateWithoutTime(firstTalkCompletionDueDate);

  const firstTalkSignedDate =
    firstTalkParticipantSignedDate && firstTalkSupervisorSignedDate
      ? dateWithoutTime(
          [firstTalkParticipantSignedDate, firstTalkSupervisorSignedDate].sort(
            (date1, date2) => date2.getTime() - date1.getTime()
          )[0]
        )
      : null;

  const firstTalkDateStatus =
    firstTalkCompletionDatePassed && firstTalkStatus && OpenTasksSubmissionStatus.includes(firstTalkStatus)
      ? "warning"
      : firstTalkCompletionDatePassed && firstTalkStatus === TalkStatus.Done
      ? "success"
      : "normal";

  const openTasksCount = React.useMemo(() => {
    return talks
      .filter(({ talkCompletionDueDate, status }) => {
        const maxCompletionDueDate = dateWithoutTime(new Date(talkCompletionDueDate));
        maxCompletionDueDate.setDate(maxCompletionDueDate.getDate() + 14);

        return currentDate < maxCompletionDueDate && ![TalkStatus.Done, TalkStatus.Cancelled].includes(status);
      })
      .map(({ supervisorSignedDate, talkQuestionnaires: openTasksQuestionnaires, peerSelection }) => {
        const peerSelectionTask = peerSelection && OpenTasksSubmissionStatus.includes(peerSelection.status);
        const openQuestionnaires = openTasksQuestionnaires.filter(
          ({ questionnaireType, submissionStatus }) =>
            SupervisorQuestionnaires.includes(questionnaireType) && OpenTasksSubmissionStatus.includes(submissionStatus)
        );

        return openQuestionnaires.length + Number(peerSelectionTask) + Number(!supervisorSignedDate);
      })
      .reduce((acc, tasks): number => acc + tasks, 0);
  }, [talks]);

  const hideGeneralData = talks.length > 1;

  // Expand user card if there are not completed talks
  const defaultOpen = React.useMemo(() => {
    return defaultCollapsed
      ? false
      : !talks.every(({ status }) => {
          return [TalkStatus.Done, TalkStatus.Cancelled].includes(status);
        });
  }, [talks, defaultCollapsed]);

  const [open, updateOpen] = React.useState(defaultOpen);
  const toggleOpen = React.useCallback(() => {
    updateOpen(!open);
  }, [open]);

  const containerClasses = React.useMemo(() => {
    const classNames = [classes.employeeContainer];
    if (open) {
      classNames.push(classes.open);
    }

    return classNames.join(" ");
  }, [open]);

  return (
    <div className={containerClasses}>
      <div className={classes.employeeDetails}>
        <div className={classes.employeeDetailsAvatar}>
          <OpenTasks count={openTasksCount}>
            <Avatar type="small" fullName={icFullName} avatar={photoBase64} />
          </OpenTasks>
        </div>
        <div className={classes.employeeDetailsName}>
          <label className={classes.employeeDetailsNameLabel} onClick={toggleOpen}>
            <Typography className={classes.title} display="inline" color="primary" variant="subtitle2">
              {icFullName}
            </Typography>
          </label>
        </div>
        {!hideGeneralData ? (
          <>
            <div className={classes.statusWrapper}>
              <Status icName={firstName} dsvName={dsvName} type="talk" status={firstTalkStatus} />
            </div>
            <div className={classes.scheduleDateWrapper} />
            <div className={classes.dateWrapper}>{firstTalkSignedDate ? displayDate(firstTalkSignedDate) : null}</div>
            <div className={classes.dateWrapper}>
              <DateStatus type={firstTalkDateStatus} date={firstTalkCompletionDueDate} />
            </div>
          </>
        ) : null}
        <div className={classes.optionsWrapper} />
        <div className={classes.expandWrapper}>
          <Fab
            onClick={toggleOpen}
            classes={{ label: classes.expandButtonLabel }}
            className={classes.fab}
            size="small"
            color="default"
          >
            <label className={classes.expandLabel} htmlFor={`employee:${id}`}>
              <Typography color="primary" className={classes.arrow}>
                <IconArrowDropDown />
              </Typography>
            </label>
          </Fab>
        </div>
      </div>
      <div className={classes.employeeCycles}>
        <Collapse in={open}>
          <div>
            {talks.map(talk => {
              const { id: talkId } = talk;
              return (
                <EmployeeCardTalk
                  dsvName={dsvName}
                  icName={firstName}
                  key={talkId}
                  onScheduleTalkDateSubmit={onScheduleTalkDateSubmit}
                  icFullName={icFullName}
                  icEmail={email}
                  onCancellation={onCancellation}
                  onReassign={onReassign}
                  onReopen={onReopen}
                  defaultCollapsed={defaultCollapsed}
                  {...talk}
                />
              );
            })}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default React.memo(Employee);
